<template>
  <div :style="{ height: size }">
    <v-skeleton-loader
      v-if="isLoading"
      type="image"
      style="border-radius: 50%"
      :width="size"
      :height="size"
    />
    <v-avatar v-else :size="`${size} !important`">
      <ImageMedia
        v-if="showImageMedia"
        class="avatar-content"
        data-private
        data-testid="image-media"
        :revMedia="accountPhoto"
        :class="{ blackandwhite: account.isDeleted }"
        @error="hasError = true"
      >
        <v-overlay absolute :value="account.isDeleted" color="white" />
      </ImageMedia>

      <div
        v-else
        class="d-flex justify-center align-center avatar-content"
        :class="backgroundColor"
      >
        <span
          class="name-letters font-weight-medium white--text"
          :style="{ 'font-size': fontSize }"
          >{{ accountLetters }}</span
        >
        <v-overlay
          absolute
          :value="isDeletedAccount"
          color="white"
          style="border-radius: 50%"
        />
      </div>
    </v-avatar>
  </div>
</template>

<script>
import MediaSDK from '@/lib/ReverMediaSDK'
import { IMAGE_SIZES } from 'rever-media-sdk'

import ImageMedia from '@/ui/shared/components/ImageMediaView.vue'

const AVATAR_COLORS = [
  'blue darken-1',
  'blue',
  'blue lighten-1',
  'purple darken-1',
  'purple',
  'purple lighten-1',
  'red darken-1',
  'red',
  'red lighten-1',
  'orange darken-1',
  'orange',
  'orange lighten-1',
  'yellow darken-1',
  'yellow',
  'yellow lighten-1',
  'green darken-1',
  'green',
  'green lighten-1',
  'teal darken-1',
  'teal',
  'teal lighten-1',
  'blue-grey darken-1',
  'blue-grey',
  'blue-grey lighten-1',
]

export default {
  name: 'UserAvatar',

  components: {
    ImageMedia,
  },

  props: {
    imageSize: {
      type: String,
      required: false,
      default: IMAGE_SIZES.SQUARE,
    },
    size: {
      type: String,
      required: false,
      default: '108px',
    },
    fontSize: {
      type: String,
      required: false,
      default: '35px',
    },
    account: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      fetchedImgSrc: null,
      isLoading: false,
      hasError: false,
    }
  },

  created() {
    this.getPhoto()
  },

  computed: {
    userHasPhoto() {
      return Boolean(this.account?.photo?._id ?? this.account?.photo?.id)
    },

    accountId() {
      return this.account?._id ?? this.account?.userId ?? this.account?.email
    },

    accountPhoto() {
      return this.account?.photo ?? null
    },

    accountLetters() {
      const displayName = this.account.displayname?.trim?.() || ''
      const familyName = this.account.familyname?.trim?.() || ''

      if (displayName && familyName) {
        return `${displayName.charAt(0)}${familyName.charAt(0)}`.toUpperCase()
      }

      if (displayName && !familyName) {
        return displayName.substring(0, 2).toUpperCase()
      }

      return this.account?.email?.substring?.(0, 2)?.toUpperCase?.()
    },

    backgroundColor() {
      let sum = 0
      for (const index in this.accountId) {
        sum += this.accountId.charCodeAt(index)
      }
      const index = sum % AVATAR_COLORS.length

      return AVATAR_COLORS[index]
    },

    isDeletedAccount() {
      return this.account?.isDeleted
    },

    showImageMedia() {
      return this.userHasPhoto && !this.hasError
    },
  },

  methods: {
    async getPhoto() {
      if (!this.userHasPhoto) return
      try {
        this.isLoading = true
        this.fetchedImgSrc = await MediaSDK.getImageSrc(
          this.accountPhoto,
          IMAGE_SIZES.SQUARE
        )
      } catch (err) {
        // console.log('ERROR', err)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.avatar-content {
  width: 100%;
  height: 100%;
}

.blackandwhite {
  filter: gray;
  filter: grayscale(100%);
}
</style>
